'use client';

import { AuthContext } from './AuthContext';
import { LicenseContextProvider } from './LicenseContext';
import { SlackIntegrationModel } from '@/ui/Setting/Integration/models/SlackIntegrationModel';
import { updateWorkspaceAction } from '@/ui/workspace/actions';
import { setCookie } from '@/utils/cookie/BrowserCookie';
import { isActionError } from 'app/util/types';
import { MemberDetailWithTeams, Workspace, WorkspaceUpdate } from 'callabo-api/src';
import { IntegrationModel } from 'libs/callabo-state/models/IntegrationModel';
import { WorkspaceModel } from 'libs/callabo-state/models/WorkspaceModel';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ApiKeyModel } from 'src/ui/Setting/Integration/models/ApiKeyModel';
import { MembersContextProvider } from './MembersContext';
import { memberDetailToMeModel, MemberMeModel, useMembersMe } from '@/hooks/useMembersMe';
import { useWorkspaceId } from 'app/[locale]/w/[workspaceId]/components/useWorkspaceHooks';
import { initChannelTalk } from '@/seo/ChannelTalkStore';
import { useSC } from './useSC';

export interface State {
  me: MemberMeModel;
  refetchMe: () => Promise<void>;
  workspace: WorkspaceModel;
  hasPermission: boolean;
  createWorkspaceAsync: (name: string, slug: string) => Promise<WorkspaceModel>;
  updateWorkspaceAsync: (updatedData: WorkspaceUpdate) => Promise<void>;
  integrations: IntegrationModel[];
  integrationForSlack: SlackIntegrationModel;
  apiKey: ApiKeyModel;
  deleteIntegrationAsync: (id: number) => Promise<void>;
  deleteIntegrationForSlackAsync: () => Promise<void>;
  createApiKeyAsync: () => Promise<void>;
  deleteApiKeyAsync: (id: number) => Promise<void>;
  isIntegrationLoaded: boolean;
}

const initialState: State = {
  me: undefined,
  refetchMe: () => undefined,
  workspace: undefined,
  hasPermission: false,
  createWorkspaceAsync: () => undefined,
  updateWorkspaceAsync: () => undefined,
  integrations: undefined,
  integrationForSlack: undefined,
  apiKey: undefined,
  deleteIntegrationAsync: () => undefined,
  deleteIntegrationForSlackAsync: () => undefined,
  createApiKeyAsync: () => undefined,
  deleteApiKeyAsync: () => undefined,
  isIntegrationLoaded: false,
};

const WorkspaceContext = createContext<State>(initialState);

export const WorkspaceContextProvider: React.FC<
  PropsWithChildren<{ rawWorkspace: Workspace; memberMe: MemberDetailWithTeams }>
> = ({ children, rawWorkspace, memberMe }) => {
  const [me, setMe] = useState<MemberMeModel>(useMembersMe(memberMe));
  const { authState } = useContext(AuthContext);
  const [hasPermission] = useState(true);
  const [workspace, setWorkspace] = useState<WorkspaceModel>(new WorkspaceModel(rawWorkspace));
  const workspaceId = useWorkspaceId();
  const lastWorkspaceRef = useRef<Workspace>();
  useEffect(() => {
    if (lastWorkspaceRef.current) {
      const lastWorkspace = lastWorkspaceRef.current;
      //하나라도 바뀐게 있다면 상태 갱신
      // TODO: SSR 제대로 도입시 사라질 로직임.
      if (
        rawWorkspace.slug !== lastWorkspace.slug ||
        rawWorkspace.name !== lastWorkspace.name ||
        rawWorkspace.status !== lastWorkspace.status ||
        rawWorkspace.created_at !== lastWorkspace.created_at ||
        rawWorkspace.updated_at !== lastWorkspace.updated_at ||
        rawWorkspace.default_access_scope !== lastWorkspace.default_access_scope ||
        rawWorkspace.default_transcribe_language !== lastWorkspace.default_transcribe_language ||
        rawWorkspace.color !== lastWorkspace.color ||
        rawWorkspace.record_lifecycle_days !== lastWorkspace.record_lifecycle_days ||
        rawWorkspace.use_2fa !== lastWorkspace.use_2fa ||
        rawWorkspace.ip_block_type !== lastWorkspace.ip_block_type ||
        rawWorkspace.option.save_media !== lastWorkspace.option.save_media ||
        rawWorkspace.option.save_dialog !== lastWorkspace.option.save_dialog ||
        rawWorkspace.option.max_device_session_count !==
          lastWorkspace.option.max_device_session_count ||
        rawWorkspace.option.media_lifecycle_days !== lastWorkspace.option.media_lifecycle_days ||
        rawWorkspace.option.dialog_lifecycle_days !== lastWorkspace.option.dialog_lifecycle_days ||
        rawWorkspace.option.provision_type !== lastWorkspace.option.provision_type
      ) {
        setWorkspace(new WorkspaceModel(rawWorkspace));
      }
    }

    lastWorkspaceRef.current = rawWorkspace;
  }, [rawWorkspace]);

  useEffect(() => {
    setCookie('last_used_workspace_id', workspaceId, 30 * 24 * 60 * 60);
  }, [workspaceId]);

  const [integrations, setIntegrations] = useState<IntegrationModel[]>([]);
  const [integrationForSlack, setIntegrationForSlack] = useState<
    SlackIntegrationModel | undefined
  >();
  const [apiKey, setApiKey] = useState<ApiKeyModel | undefined>();
  const [isIntegrationLoaded, setIsIntegrationLoaded] = useState(false);
  /**
   * 활성화된 메인 워크스페이스에 연동된 서비스 목록을 가져오는 과정
   */
  useEffect(() => {
    (async () => {
      if (!workspaceId || authState.type !== 'signedIn' || !hasPermission) return;
      const { callaboApiClient } = authState;
      try {
        const calendar =
          await callaboApiClient.integration.listIntegrationV1WorkspaceSlugIntegrationGet(
            workspaceId
          );
        setIntegrations(calendar.map((integration) => new IntegrationModel(integration)));
      } catch (error) {
        setIntegrations([]);
      }
      try {
        const slackChannel =
          await callaboApiClient.slackChannel.getSlackChannelIntegrationV1WorkspaceSlugIntegrationsSlackGet(
            workspaceId
          );
        setIntegrationForSlack(slackChannel ? new SlackIntegrationModel(slackChannel) : undefined);
      } catch (error) {
        setIntegrationForSlack(undefined);
      }
      try {
        const apiKey = await callaboApiClient.apiKey.getApiKeyV1WorkspaceSlugApiKeyGet(workspaceId);
        setApiKey(apiKey ? new ApiKeyModel(apiKey) : undefined);
      } catch (error) {
        setApiKey(undefined);
      }
      setIsIntegrationLoaded(true);
    })();
  }, [workspaceId, authState.type, hasPermission]);

  const isValidSC = useSC(['e', 'i']);
  useEffect(() => {
    initChannelTalk(isValidSC);
  }, [isValidSC]);

  const createWorkspaceAsync = useCallback(
    async (name: string, slug: string): Promise<WorkspaceModel> => {
      if (authState.type !== 'signedIn') return;
      const { callaboApiClient } = authState;
      const response = await callaboApiClient.workspace.createWorkspaceV1WorkspacePost({
        name,
        slug,
      });
      const newWorkspace = new WorkspaceModel(response);
      setWorkspace(newWorkspace);
      return newWorkspace;
    },
    [authState]
  );

  const updateWorkspaceAsync = useCallback(
    async (updatedData: WorkspaceUpdate): Promise<void> => {
      if (!workspaceId || authState.type !== 'signedIn') return;
      const actionResult = await updateWorkspaceAction(workspaceId, updatedData);
      if (isActionError(actionResult)) {
        throw new Error('Failed to update workspace');
      }
    },
    [authState, workspaceId]
  );

  const deleteIntegrationAsync = useCallback(
    async (id: number) => {
      if (!workspaceId || authState.type !== 'signedIn') return;
      const { callaboApiClient } = authState;
      await callaboApiClient.integration.deleteIntegrationV1WorkspaceSlugIntegrationsIdDelete(
        id,
        workspaceId
      );
      setIntegrations((integrations) => integrations.filter((item) => item.id !== id));
    },
    [workspaceId, authState]
  );

  const deleteIntegrationForSlackAsync = useCallback(async () => {
    if (authState.type !== 'signedIn' || !workspaceId) return;
    const { callaboApiClient } = authState;
    try {
      await callaboApiClient.slackChannel.deleteSlackIntegrationV1WorkspaceSlugIntegrationsSlackDelete(
        workspaceId
      );
      setIntegrationForSlack(undefined);
    } catch (e) {
      if (e.status === 404) {
        setIntegrationForSlack(undefined);
        return;
      }
      throw e;
    }
  }, [authState, workspaceId]);

  const createApiKeyAsync = useCallback(async () => {
    if (authState.type !== 'signedIn' || !workspaceId) return;
    const { callaboApiClient } = authState;
    const response = await callaboApiClient.apiKey.createApiKeyV1WorkspaceSlugApiKeyPut(
      workspaceId
    );
    setApiKey(new ApiKeyModel(response));
  }, [authState, apiKey, workspaceId]);

  const deleteApiKeyAsync = useCallback(
    async (id: number) => {
      if (authState.type !== 'signedIn' || !workspaceId) return;
      const { callaboApiClient } = authState;
      try {
        await callaboApiClient.apiKey.deleteApiKeyV1WorkspaceSlugApiKeyIdDelete(id, workspaceId);
        setApiKey(undefined);
      } catch (e) {
        if (e.status === 404) {
          setApiKey(undefined);
        }
        throw e;
      }
    },
    [authState, apiKey, workspaceId]
  );

  const refetchMe = async () => {
    if (authState.type !== 'signedIn' || !workspaceId) return;
    const { callaboApiClient } = authState;
    try {
      const newMe = await await callaboApiClient.members.getMeV1WorkspacesSlugMembersMeGet(
        workspaceId
      );
      setMe(memberDetailToMeModel(newMe));
    } catch (e) {
      // pass
    }
  };

  return (
    <WorkspaceContext.Provider
      value={{
        me,
        refetchMe,
        workspace,
        createWorkspaceAsync,
        updateWorkspaceAsync,
        hasPermission,
        integrations,
        integrationForSlack,
        apiKey,
        deleteIntegrationAsync,
        deleteIntegrationForSlackAsync,
        createApiKeyAsync,
        deleteApiKeyAsync,
        isIntegrationLoaded,
      }}
    >
      <MembersContextProvider>
        <LicenseContextProvider workspace={hasPermission ? workspace : undefined}>
          {children}
        </LicenseContextProvider>
      </MembersContextProvider>
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
